import { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Star, Zap, Crown, Building, Mail, AlertCircle, Headphones, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import useStore from '../store/useStore';
import { PAYMENT_TIERS_API, fetchAPI } from '../config';

function Pricing() {
  const [billingCycle, setBillingCycle] = useState('yearly');
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(null);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [error, setError] = useState('');
  const { user, accessTier } = useStore();
  const navigate = useNavigate();

  useEffect(() => {
    fetchSubscriptionTiers();
  }, []);

  const fetchSubscriptionTiers = async () => {
    try {
      const data = await fetchAPI('/api/v2/admin/payment-tiers.php');
      
      const processedTiers = data.map(tier => {
        let featuresArray = [];
        
        if (tier.features) {
          if (Array.isArray(tier.features)) {
            featuresArray = tier.features.map(feature => {
              if (typeof feature === 'string') {
                return { name: feature, included: true };
              }
              return feature;
            });
          } else if (typeof tier.features === 'string') {
            featuresArray = tier.features.split('|').map(feature => ({
              name: feature.trim(),
              included: true
            }));
          }
        }
        
        return {
          ...tier,
          features: featuresArray,
          monthly_price: Number(tier.monthly_price) || 0,
          yearly_price: Number(tier.yearly_price) || 0,
          yearly_only: tier.name === 'pro'
        };
      });
      
      setTiers(processedTiers);
    } catch (error) {
      console.warn('API failed, using mock tiers:', error);
      setTiers(getMockTiers());
    } finally {
      setLoading(false);
    }
  };

  const getMockTiers = () => [
    {
      id: 1,
      name: 'free',
      display_name: 'Saints Free',
      description: 'Perfect for getting started with AI learning',
      monthly_price: 0,
      yearly_price: 0,
      currency: 'ZAR',
      features: [
        { name: 'Unlimited podcast access', included: true },
        { name: 'Podcast series collections', included: true },
        { name: 'Weekly newsletter', included: true },
        { name: 'Standard audio quality', included: true },
        { name: 'Ad-free listening', included: true },
        { name: 'HD audio quality', included: false },
        { name: 'Downloadable content', included: false },
        { name: 'Certifications (Coming Soon)', included: false },
        { name: 'Premium Courses (Coming Soon)', included: false }
      ],
      popular: false
    },
    {
      id: 2,
      name: 'plus',
      display_name: 'Saints Plus',
      description: 'Enhanced learning with premium content (Coming Soon)',
      monthly_price: 99,
      yearly_price: 990,
      currency: 'ZAR',
      features: [
        { name: 'Everything in Free', included: true },
        { name: '2 premium courses monthly (Coming Soon)', included: true },
        { name: 'HD audio quality', included: true },
        { name: 'Downloadable transcripts', included: true },
        { name: 'Early access to new podcasts', included: true },
        { name: 'Mobile app downloads', included: true },
        { name: 'Certifications (Coming Soon)', included: false },
        { name: 'Advanced learning paths', included: false }
      ],
      popular: true,
      comingSoon: true
    },
    {
      id: 3,
      name: 'pro',
      display_name: 'Saints Pro',
      description: 'Complete learning experience (Coming Soon)',
      monthly_price: 0,
      yearly_price: 3990,
      currency: 'ZAR',
      features: [
        { name: 'Everything in Plus', included: true },
        { name: '8 premium courses monthly (Coming Soon)', included: true },
        { name: 'All certifications included (Coming Soon)', included: true },
        { name: 'Custom learning paths', included: true },
        { name: 'LinkedIn badge integration', included: true },
        { name: 'Lifetime course access', included: true },
        { name: 'Priority support', included: true },
        { name: 'Exclusive community access', included: true }
      ],
      popular: false,
      comingSoon: true,
      yearly_only: true
    },
    {
      id: 4,
      name: 'business',
      display_name: 'Saints for Business',
      description: 'Enterprise-grade learning (Coming Soon)',
      currency: 'ZAR',
      features: [
        { name: 'Everything in Saints Pro for all users', included: true },
        { name: 'Centralized User Management & Billing', included: true },
        { name: 'Customizable Onboarding Paths', included: true },
        { name: 'Company-Wide Dashboard & Analytics', included: true },
        { name: 'Shared Team Workspace', included: true },
        { name: 'Group analytics and progress tracking', included: true },
        { name: 'Quarterly team training reports and certification', included: true },
        { name: 'Dedicated account manager', included: true },
        { name: 'SLA guarantee', included: true }
      ],
      popular: false,
      customPrice: true,
      comingSoon: true
    }
  ];

  const handleSubscribe = async (tier) => {
    if (!user) {
      localStorage.setItem('lastVisitedPage', '/pricing');
      navigate('/auth');
      return;
    }

    const token = localStorage.getItem('token');
    
    if (!token) {
      alert('Session expired. Please login again.');
      navigate('/auth');
      return;
    }

    if (tier.name === 'free') {
      try {
        const response = await fetch('/api/v2/payments/subscribe/free', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          useStore.getState().setAccessTier(tier.name);
          alert(data.message || 'Successfully enrolled in Free tier!');
          window.location.reload();
        } else {
          const errorText = await response.text();
          alert('Failed to enroll in Free tier: ' + (errorText || 'Unknown error'));
        }
      } catch (error) {
        alert('Enrollment failed: ' + error.message);
      }
      return;
    }

    if (tier.name === 'business') {
      navigate('/contact-sales', { 
        state: { 
          tier: tier,
          interest: 'business_tier'
        }
      });
      return;
    }

    navigate('/checkout', { 
      state: { 
        tier: tier,
        amount: billingCycle === 'monthly' ? tier.monthly_price : tier.yearly_price,
        billingCycle 
      }
    });
  };

  const handleNotifyMe = async (e, tierName = 'plus') => {
    e.preventDefault();
    
    const subscribeEmail = email || user?.email;
    
    if (!subscribeEmail) {
      setError('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(subscribeEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    setNotificationLoading(true);
    setError('');
    
    try {
      const response = await fetch('/api/v2/admin/email-api.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'subscribe',
          email: subscribeEmail,
          name: user?.name || '',
          type: tierName === 'pro' ? 'saints_pro' : 'saints_plus',
          tier: tierName,
          source: 'pricing_page',
          user_id: user?.id || null,
          meta: {
            page: 'pricing',
            tier: tierName,
            timestamp: new Date().toISOString()
          }
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setSubscribed(tierName);
        setEmail('');
        
        setTimeout(() => {
          setSubscribed(null);
        }, 5000);
      } else {
        await tryPricingBackup(subscribeEmail, tierName);
      }
      
    } catch (error) {
      await tryPricingBackup(subscribeEmail, tierName);
    } finally {
      setNotificationLoading(false);
    }
  };

  const tryPricingBackup = async (subscribeEmail, tierName) => {
    try {
      const backupResponse = await fetch('/api/v2/admin/pricing-notify.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: subscribeEmail,
          name: user?.name || '',
          tier: tierName,
          type: tierName === 'pro' ? 'saints_pro' : 'saints_plus',
          user_id: user?.id || null
        })
      });
      
      const backupData = await backupResponse.json();
      
      if (backupData.success) {
        setSubscribed(tierName);
        setEmail('');
        
        setTimeout(() => {
          setSubscribed(null);
        }, 5000);
      } else {
        setError('Failed to subscribe. Please try again later.');
      }
    } catch (backupError) {
      setError('Failed to subscribe. Please try again later.');
    }
  };

  const getTierIcon = (tierName) => {
    switch (tierName) {
      case 'free': return Star;
      case 'plus': return Zap;
      case 'pro': return Crown;
      case 'business': return Building;
      default: return Star;
    }
  };

  const getButtonText = (tier) => {
    if (!user) return 'Get Started';
    if (accessTier === tier.name) return 'Current Plan';
    if (tier.name === 'free') return 'Enroll Free';
    if (tier.name === 'business') return 'Contact Sales';
    return 'Notify Me';
  };

  const getButtonStyle = (tier) => {
    if (accessTier === tier.name) {
      return 'bg-gray-200 text-gray-500 cursor-not-allowed';
    }
    
    if (tier.name === 'business') {
      return 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg';
    }
    
    if (tier.name === 'free') {
      return 'bg-green-600 text-white hover:bg-green-700 hover:shadow-lg';
    }
    
    return 'bg-gradient-to-r from-yellow-500 to-amber-500 text-white hover:shadow-lg hover:scale-105';
  };

  const getPriceDisplay = (tier) => {
    if (tier.customPrice || tier.monthly_price === null || tier.yearly_price === null) {
      return { price: 'Custom Pricing', period: 'Contact sales for team pricing' };
    }
    
    if (tier.yearly_only) {
      return { 
        price: `R${tier.yearly_price}`, 
        period: '/year'
      };
    }
    
    const price = billingCycle === 'monthly' ? tier.monthly_price : tier.yearly_price;
    const period = billingCycle === 'monthly' ? '/month' : '/year';
    
    return { price: `R${price}`, period };
  };

  const calculateSavings = (tier) => {
    if (tier.monthly_price === null || tier.yearly_price === null || tier.monthly_price === 0 || tier.yearly_price === 0) return 0;
    const yearlyTotal = tier.monthly_price * 12;
    const savings = yearlyTotal - tier.yearly_price;
    return Math.round((savings / yearlyTotal) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading pricing plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Choose Your Learning Journey
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Select the perfect plan for your AI education needs. Enjoy 130+ podcasts for free while premium features are in development.
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center bg-white rounded-lg p-1 shadow-sm mt-8">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-3 rounded-md font-medium transition-colors duration-200 ${
                billingCycle === 'monthly'
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-6 py-3 rounded-md font-medium transition-colors duration-200 ${
                billingCycle === 'yearly'
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Yearly
              <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                Save up to 20%
              </span>
            </button>
          </div>

          {/* Coming Soon Banner */}
          <div className="mt-8 max-w-2xl mx-auto">
            <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 rounded-xl p-6 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <AlertCircle className="w-5 h-5 text-yellow-600" />
                <span className="font-semibold text-yellow-700">Important Notice</span>
              </div>
              <p className="text-yellow-800">
                <strong>Saints Plus, Pro, and Business tiers are coming soon!</strong> Enjoy our extensive podcast library for free while we prepare premium courses and features.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
          {tiers.map((tier, index) => {
            const Icon = getTierIcon(tier.name);
            const isCurrentTier = user && accessTier === tier.name;
            const savings = calculateSavings(tier);
            const priceDisplay = getPriceDisplay(tier);
            const featuresArray = Array.isArray(tier.features) ? tier.features : [];

            return (
              <motion.div
                key={tier.id || index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`relative bg-white rounded-2xl shadow-lg overflow-hidden ${
                  tier.popular ? 'ring-2 ring-purple-600 transform scale-105 z-10' : ''
                } ${tier.name === 'business' ? 'border-2 border-blue-500' : ''}`}
              >
                {/* Popular Badge */}
                {tier.popular && (
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2">
                    <span className="bg-purple-600 text-white px-4 py-1 rounded-b-lg text-sm font-medium">
                      Most Popular
                    </span>
                  </div>
                )}

                {/* Yearly-only Badge */}
                {tier.yearly_only && (
                  <div className="absolute top-0 right-4 transform translate-y-2">
                    <span className="bg-green-500 text-white px-3 py-1 rounded-lg text-xs font-medium flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Yearly Only
                    </span>
                  </div>
                )}

                {/* Business Badge */}
                {tier.name === 'business' && (
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 z-20">
                    <span className="bg-blue-600 text-white px-4 py-1 rounded-b-lg text-sm font-medium">
                      Enterprise
                    </span>
                  </div>
                )}

                <div className="p-8">
                  {/* Tier Header */}
                  <div className="flex items-center space-x-3 mb-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      tier.name === 'business' 
                        ? 'bg-gradient-to-br from-blue-500 to-blue-700' 
                        : tier.popular
                        ? 'bg-gradient-to-br from-purple-600 to-indigo-600'
                        : 'bg-gradient-to-br from-gray-700 to-gray-900'
                    }`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">{tier.display_name}</h3>
                      <p className="text-gray-600 text-sm">{tier.description}</p>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="mb-6">
                    <div className="flex items-baseline">
                      <span className={`${tier.customPrice ? 'text-2xl' : 'text-4xl'} font-bold text-gray-900`}>
                        {priceDisplay.price}
                      </span>
                      {!tier.customPrice && priceDisplay.price !== 'Custom Pricing' && (
                        <span className="text-gray-600 ml-2">
                          {priceDisplay.period}
                        </span>
                      )}
                    </div>
                    
                    {tier.customPrice && (
                      <p className="text-gray-600 text-sm mt-1">
                        {priceDisplay.period}
                      </p>
                    )}
                    
                    {!tier.customPrice && savings > 0 && billingCycle === 'yearly' && !tier.yearly_only && (
                      <p className="text-green-600 text-sm mt-1">
                        Save {savings}% vs monthly
                      </p>
                    )}
                    
                    {tier.yearly_only && (
                      <p className="text-green-600 text-sm mt-1 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Yearly subscription only
                      </p>
                    )}
                  </div>

                  {/* Subscribe Button */}
                  <button
                    onClick={() => handleSubscribe(tier)}
                    disabled={isCurrentTier}
                    className={`w-full h-12 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center ${getButtonStyle(tier)}`}
                  >
                    {getButtonText(tier)}
                    {isCurrentTier && (
                      <CheckCircle className="w-5 h-5 ml-2" />
                    )}
                    {(tier.name === 'plus' || tier.name === 'pro') && (
                      <Mail className="w-5 h-5 ml-2" />
                    )}
                  </button>

                  {/* Features List */}
                  <div className="mt-8 space-y-3">
                    <h4 className="font-semibold text-gray-900 mb-2">What's included:</h4>
                    {featuresArray.length > 0 ? (
                      featuresArray.map((feature, featureIndex) => {
                        const featureName = typeof feature === 'string' ? feature : feature.name;
                        const isIncluded = typeof feature === 'object' ? (feature.included !== false) : true;
                        const isComingSoon = featureName.toLowerCase().includes('coming soon');
                        
                        return (
                          <div key={featureIndex} className="flex items-start space-x-3">
                            {isIncluded ? (
                              isComingSoon ? (
                                <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                              ) : (
                                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                              )
                            ) : (
                              <XCircle className="w-5 h-5 text-gray-300 flex-shrink-0 mt-0.5" />
                            )}
                            <span className={`text-sm ${isIncluded ? isComingSoon ? 'text-yellow-700' : 'text-gray-700' : 'text-gray-500 line-through'}`}>
                              {featureName}
                            </span>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-gray-500 text-sm">No features listed</p>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Notification Section - This was missing! */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="max-w-2xl mx-auto mt-16"
        >
          <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-2xl p-8 border border-primary/20">
            <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Get Early Access to Premium Features</h3>
            
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm text-center">
                {error}
              </div>
            )}
            
            {subscribed ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <div className="flex items-center justify-center gap-3 mb-3">
                  <Mail className="w-6 h-6 text-green-600" />
                  <span className="font-semibold text-green-800">
                    {subscribed === 'pro' ? "You're on the Saints Pro waitlist!" : "You're on the Saints Plus waitlist!"}
                  </span>
                </div>
                <p className="text-green-700 text-center">
                  🎉 We'll email you when {subscribed === 'pro' ? 'Saints Pro' : 'Saints Plus'} launches!
                </p>
              </div>
            ) : (
              <form onSubmit={(e) => handleNotifyMe(e, 'plus')} className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={user?.email || 'Enter your email for updates'}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    required={!user?.email}
                  />
                  <button
                    type="submit"
                    disabled={notificationLoading}
                    className="px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {notificationLoading ? (
                      <span className="flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Submitting...
                      </span>
                    ) : (
                      'Notify Me for Saints Plus'
                    )}
                  </button>
                </div>
                {user?.email && (
                  <p className="text-sm text-gray-600 text-center">
                    Using your account email: {user.email}
                  </p>
                )}
                <p className="text-gray-500 text-sm text-center">
                  We'll email you when Saints Plus launches with premium courses and features.
                </p>
                
                {/* Separate Pro notification */}
                <div className="pt-4 border-t text-center">
                  <p className="text-sm text-gray-600 mb-2">Interested specifically in Saints Pro?</p>
                  <button
                    type="button"
                    onClick={(e) => handleNotifyMe(e, 'pro')}
                    disabled={notificationLoading}
                    className="text-sm text-primary hover:underline font-medium"
                  >
                    Notify me for Saints Pro
                  </button>
                </div>
              </form>
            )}
          </div>
        </motion.div>

        {/* Free Content Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="max-w-4xl mx-auto mt-12"
        >
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 border border-blue-200">
            <div className="flex items-center gap-3 mb-6">
              <Headphones className="w-8 h-8 text-blue-600" />
              <h3 className="text-2xl font-bold text-gray-900">Enjoy Free Content Now</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h4 className="font-bold text-gray-900 mb-3">130+ AI-Generated Podcasts</h4>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Cutting-edge technology topics</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Business and entrepreneurship insights</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Personal development content</span>
                  </li>
                </ul>
                <button
                  onClick={() => navigate('/podcasts')}
                  className="mt-4 w-full py-3 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors duration-200"
                >
                  Browse All Podcasts
                </button>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h4 className="font-bold text-gray-900 mb-3">Curated Podcast Series</h4>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Structured learning paths</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Topic-based collections</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Progressive difficulty levels</span>
                  </li>
                </ul>
                <button
                  onClick={() => navigate('/podcast-series')}
                  className="mt-4 w-full py-3 bg-purple-500 text-white rounded-lg font-medium hover:bg-purple-600 transition-colors duration-200"
                >
                  Explore Series
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* FAQ Section */}
        <div className="max-w-4xl mx-auto mt-20">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Frequently Asked Questions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                question: "What's available for free?",
                answer: "You get unlimited access to 130+ AI-generated podcasts, curated series, and our weekly newsletter - all completely free."
              },
              {
                question: "When will Saints Plus be available?",
                answer: "We're actively developing premium courses and features. Subscribe for notifications to be the first to know when we launch."
              },
              {
                question: "Why is Saints Pro yearly-only?",
                answer: "Saints Pro is designed as a comprehensive annual learning program with certifications and lifetime access to courses."
              },
              {
                question: "What happens to my access if I downgrade?",
                answer: "You'll lose access to premium features and new courses, but you'll keep any certifications earned and access to podcasts."
              },
              {
                question: "How do I apply for the Ambassador Program?",
                answer: "Visit our Ambassador Program page to apply for Saints Pro access by helping promote Saints Academy."
              },
              {
                question: "What will be included in Saints Plus?",
                answer: "Premium courses, HD audio, downloadable transcripts, early access to new content, and more advanced features."
              },
              {
                question: "Will there be certifications?",
                answer: "Yes, certifications are planned for Saints Pro tier. They'll be available once courses are launched."
              },
              {
                question: "How does Business tier work?",
                answer: "Business tier includes team management, analytics, shared workspaces, and dedicated support. Contact sales for custom pricing."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                <h3 className="font-semibold text-gray-900 mb-2">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Ambassador Program CTA */}
        <div className="max-w-4xl mx-auto mt-16 text-center">
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">Want Saints Pro for Free?</h3>
            <p className="text-purple-100 mb-6 max-w-2xl mx-auto">
              Join our Ambassador Program and get full Saints Pro access by helping promote Saints Academy.
            </p>
            <button
              onClick={() => navigate('/ambassador-program')}
              className="bg-white text-purple-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
            >
              Apply for Ambassador Program
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Pricing;